import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsDisputesV1DisputeServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/payments/disputes/v1/disputes',
        destPath: '/v1/disputes',
      },
      {
        srcPath: '/payments/disputes/v1/bulk/disputes',
        destPath: '/v1/bulk/disputes',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/payments/disputes/v1/disputes',
        destPath: '/v1/disputes',
      },
      {
        srcPath: '/payments/disputes/v1/bulk/disputes',
        destPath: '/v1/bulk/disputes',
      },
    ],
    _: [
      {
        srcPath: '/payments/disputes/v1/disputes',
        destPath: '/v1/disputes',
      },
      {
        srcPath: '/payments/disputes/v1/bulk/disputes',
        destPath: '/v1/bulk/disputes',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_disputes';

/** Retrieves a dispute. */
export function getDispute(payload: object): RequestOptionsFactory<any> {
  function __getDispute({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.disputes.v1.dispute',
      method: 'GET' as any,
      methodFqn: 'wix.payments.disputes.v1.DisputeService.GetDispute',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputesV1DisputeServiceUrl({
        protoPath: '/v1/disputes/{disputeId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'dispute.dueDate' },
              { path: 'dispute.defendedDate' },
              { path: 'dispute.acceptedDate' },
              { path: 'dispute.createdDate' },
              { path: 'dispute.updatedDate' },
              { path: 'dispute.actions.dueDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDispute;
}

/**
 * Creates a query to retrieve a list of disputes.
 *
 * The Query Disputes method builds a query to retrieve a list of disputes and returns a `DisputesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `DisputesQueryBuilder` methods onto the query. `DisputesQueryBuilder` methods enable you to filter, sort, and control the results that Query Disputes returns.
 *
 * Query Disputes has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `DisputesQueryResult`.
 */
export function queryDisputes(payload: object): RequestOptionsFactory<any> {
  function __queryDisputes({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.disputes.v1.dispute',
      method: 'GET' as any,
      methodFqn: 'wix.payments.disputes.v1.DisputeService.QueryDisputes',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputesV1DisputeServiceUrl({
        protoPath: '/v1/disputes/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'disputes.dueDate' },
              { path: 'disputes.defendedDate' },
              { path: 'disputes.acceptedDate' },
              { path: 'disputes.createdDate' },
              { path: 'disputes.updatedDate' },
              { path: 'disputes.actions.dueDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixPaymentsDisputesV1DisputeServiceUrl({
            protoPath: '/v1/disputes/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryDisputes;
}

/**
 * Accepts liability for a dispute, indicating the merchant agrees with the customer's claim.
 *
 * When a dispute is accepted, the refund is processed automatically.
 */
export function acceptDispute(payload: object): RequestOptionsFactory<any> {
  function __acceptDispute({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.disputes.v1.dispute',
      method: 'POST' as any,
      methodFqn: 'wix.payments.disputes.v1.DisputeService.AcceptDispute',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputesV1DisputeServiceUrl({
        protoPath: '/v1/disputes/{disputeId}/accept',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'dispute.dueDate' },
              { path: 'dispute.defendedDate' },
              { path: 'dispute.acceptedDate' },
              { path: 'dispute.createdDate' },
              { path: 'dispute.updatedDate' },
              { path: 'dispute.actions.dueDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __acceptDispute;
}

/**
 * Defends a dispute by submitting evidence to the payment service provider or bank handling the dispute.
 *
 * To upload evidence documents, use the [Dispute Evidence Documents API](https://dev.wix.com/docs/api-reference/business-management/payments/disputes/dispute-evidence-documents/create-dispute-evidence-document). This method submits all the evidence documents uploaded for the specified dispute.
 */
export function defendDispute(payload: object): RequestOptionsFactory<any> {
  function __defendDispute({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.disputes.v1.dispute',
      method: 'POST' as any,
      methodFqn: 'wix.payments.disputes.v1.DisputeService.DefendDispute',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputesV1DisputeServiceUrl({
        protoPath: '/v1/disputes/{disputeId}/defend',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'dispute.dueDate' },
              { path: 'dispute.defendedDate' },
              { path: 'dispute.acceptedDate' },
              { path: 'dispute.createdDate' },
              { path: 'dispute.updatedDate' },
              { path: 'dispute.actions.dueDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __defendDispute;
}

/**
 * Updates tags on multiple disputes simultaneously using a list of dispute IDs.
 * If a tag appears in both assign and unassign lists, it will be assigned.
 */
export function bulkUpdateDisputeTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateDisputeTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.disputes.v1.dispute',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.disputes.v1.DisputeService.BulkUpdateDisputeTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputesV1DisputeServiceUrl({
        protoPath: '/v1/bulk/disputes/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateDisputeTags;
}

/**
 * Asynchronously updates tags on multiple disputes based on filter criteria.
 * Returns a job ID that can be used to track the operation status. If a tag appears in both assign and unassign lists, it will be assigned.
 */
export function bulkUpdateDisputeTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateDisputeTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.disputes.v1.dispute',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.disputes.v1.DisputeService.BulkUpdateDisputeTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputesV1DisputeServiceUrl({
        protoPath: '/v1/bulk/disputes/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateDisputeTagsByFilter;
}
